import type {
  MessengerCustomColorTheme,
  MessengerPaletteColorTheme,
  MessengerSettings,
  MessengerSharedSettings,
} from '@widgets/messenger/types'
import { computed, inject, provide, type Ref } from '@/composition/vue/compositionApi'
import type { RefOrGetter } from '@/types'
import { getValueFromRefOrGetter } from '@/composition/getValueFromRefOrGetter'
import { WebWidgetThemeColorTypes } from '@/data/models/domain/messages/settings/webWidget/types'

const WIDGET_SHARED_SETTINGS_SYMBOL = Symbol('widgetSharedSettings')

export const provideWidgetSharedSettings = (settings: RefOrGetter<MessengerSharedSettings>) => {
  const settingsRef = computed(() => getValueFromRefOrGetter(settings))
  provide(WIDGET_SHARED_SETTINGS_SYMBOL, settingsRef)
}

export const useWidgetSharedSettings = () => {
  const settings = inject<Ref<MessengerSharedSettings>>(WIDGET_SHARED_SETTINGS_SYMBOL)
  if (!settings) {
    throw new Error('Widget shared settings not found, use provideWidgetSharedSettings first')
  }
  return settings
}

export const makeWidgetSharedSettings = (settings: MessengerSettings) => {
  const { type, color, custom } = settings.appearance.colorTheme
  const theme =
    type === WebWidgetThemeColorTypes.CUSTOM
      ? ({
          type,
          ...custom,
        } satisfies MessengerCustomColorTheme)
      : ({
          type,
          color,
        } satisfies MessengerPaletteColorTheme)

  const { alignTo, sideSpacing, bottomSpacing } = settings.appearance.minimizedState
  const activeChannels = Object.values(settings.channels).filter((channel) => channel.isActive)
  const hasOnlyOneActiveChannel = activeChannels.length === 1

  return {
    theme,
    placement: {
      alignTo,
      sideSpacing,
      bottomSpacing,
    },
    hasOnlyOneActiveChannel,
  } satisfies MessengerSharedSettings
}
